import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import api from "../../../services/api";
import { adminApi } from "../../adminApi";
import { setToastOk } from "../main/mainSlice";

const initialState = {
  paragraphs: undefined,
  paragraphDetail: undefined,
  currentPage: 1,
  itemsPerPage: 10,
  totalPages: 0,
  query: {},
};

export const fetchParagraphs = createAsyncThunk(
  "paragraphs/fetchParagraphs",
  async (data, { dispatch, rejectWithValue }) => {
    try {
      var query = "";
      Object.keys(data).forEach((key) => {
        if (data[key]) {
          if (query !== "") {
            query += `&`;
          }
          query += `${key}=${data[key]}`;
        }
      });
      const response = await api.get(`/paragraphs/search?${query}`);
      return response.data;
    } catch (error) {
      dispatch(
        setToastOk({
          isOk: false,
          messages: error.response.data,
        }),
      );
      return rejectWithValue(error.response.data);
    }
  },
);

export const fetchParagraphDetail = createAsyncThunk(
  "paragraphs/fetchParagraphDetail",
  async ({ slug }, { dispatch, rejectWithValue }) => {
    try {
      const response = await api.get(`/paragraphs/${encodeURIComponent(slug)}`);
      return response.data.data;
    } catch (error) {
      dispatch(
        setToastOk({
          isOk: false,
          messages: error.response.data,
        }),
      );
      return rejectWithValue(error.response.data);
    }
  },
);

export const createParagraph = createAsyncThunk(
  "paragraphs/createParagraph",
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      const response = await api.post(`/paragraphs`, payload);
      dispatch(adminApi.util.invalidateTags(["Paragraphs"]));
      dispatch(
        setToastOk({
          isOk: true,
          messages: response.data.message,
        }),
      );

      return response.data.data;
    } catch (error) {
      dispatch(
        setToastOk({
          isOk: false,
          messages: error.response.data,
        }),
      );
      return rejectWithValue(error.response.data);
    }
  },
);

export const editParagraph = createAsyncThunk(
  "paragraphs/editParagraph",
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      const { originalSlug, ...updates } = payload;
      const response = await api.patch(`/paragraphs/${encodeURIComponent(originalSlug)}`, updates);
      dispatch(adminApi.util.invalidateTags(["Paragraphs"]));
      dispatch(
        setToastOk({
          isOk: true,
          messages: response.data.message,
        }),
      );

      return response.data;
    } catch (error) {
      dispatch(
        setToastOk({
          isOk: false,
          messages: error.response.data,
        }),
      );
      return rejectWithValue(error.response.data);
    }
  },
);

export const deleteParagraph = createAsyncThunk(
  "paragraphs/deleteParagraph",
  async ({ slug }, { dispatch, rejectWithValue }) => {
    try {
      const response = await api.delete(`/paragraphs/${encodeURIComponent(slug)}`);
      dispatch(
        setToastOk({
          isOk: true,
          messages: response.data.message,
        }),
      );
      dispatch(adminApi.util.invalidateTags(["Paragraphs"]));
      return response.data;
    } catch (error) {
      dispatch(
        setToastOk({
          isOk: false,
          messages: error.response?.data || error.message,
        }),
      );
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const paragraphsSlice = createSlice({
  name: "paragraphs",
  initialState,
  reducers: {
    removeParagraph: (state, action) => {
      state.paragraphDetail = undefined;
    },
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload;
    },
    setQuery: (state, action) => {
      state.query = action.payload;
    },
  },
  extraReducers: (paragraphs) => {
    paragraphs
      .addCase(fetchParagraphs.pending, (state) => {})
      .addCase(fetchParagraphs.fulfilled, (state, action) => {
        state.paragraphs = action.payload.data;
        state.totalPages = action.payload.pagination.totalPages;
      })
      .addCase(fetchParagraphs.rejected, (state, action) => {})
      .addCase(fetchParagraphDetail.pending, (state) => {})
      .addCase(fetchParagraphDetail.fulfilled, (state, action) => {
        state.paragraphDetail = action.payload;
      })
      .addCase(fetchParagraphDetail.rejected, (state, action) => {})
      .addCase(createParagraph.pending, (state) => {})
      .addCase(createParagraph.fulfilled, (state, action) => {
        state.paragraphs = state.paragraphs
          ? [action.payload, ...state.paragraphs]
          : [action.payload];
      })
      .addCase(createParagraph.rejected, (state, action) => {})
      .addCase(editParagraph.pending, (state) => {})
      .addCase(editParagraph.fulfilled, (state, action) => {
        const editedParagraph = action.payload.data;
        state.paragraphs?.forEach((paragraph) => {
          if (paragraph._id === editedParagraph._id) {
            Object.keys(paragraph).forEach((key) => {
              paragraph[key] = editedParagraph[key];
            });
          }
        });
      })
      .addCase(editParagraph.rejected, (state, action) => {})
      .addCase(deleteParagraph.pending, (state) => {})
      .addCase(deleteParagraph.fulfilled, (state, action) => {
        const deletedSlug = action.payload.data?.slug;
        state.paragraphs = state.paragraphs?.filter(
          (paragraph) => paragraph.slug !== deletedSlug,
        );
      })
      .addCase(deleteParagraph.rejected, (state, action) => {});
  },
});

export const {
  removeParagraph,
  setCurrentPage,
  setQuery,
} = paragraphsSlice.actions;

export default paragraphsSlice.reducer;
